import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  LifeBuoy, Plus, Search, Send, Loader2, X, CheckCircle,
  AlertCircle, MessageSquare, Inbox, ArrowLeft, RefreshCw,
  Paperclip, FileText, Image as ImageIcon, Download,
} from 'lucide-react';
import { ticketService, TicketData, CreateTicketRequest } from '../services/ticketService';
import { useLocalization } from '../contexts/LocalizationContext';
import { useAuth } from '../contexts/AuthContext';
import LoadingScreen from './LoadingScreen';

interface Attachment {
  name: string;
  type: string;
  size: number;
  data_url: string;
}

const MAX_ATTACHMENTS = 3;
const MAX_FILE_BYTES = 2 * 1024 * 1024; // 2MB

function formatBytes(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result || ''));
    r.onerror = () => reject(r.error || new Error('read failed'));
    r.readAsDataURL(file);
  });
}

type Status = 'open' | 'in_progress' | 'resolved' | 'closed';
type Priority = 'low' | 'medium' | 'high' | 'urgent';

const STATUS_BADGE: Record<Status, string> = {
  open: 'bg-amber-100 text-amber-800 border-amber-200',
  in_progress: 'bg-blue-100 text-blue-800 border-blue-200',
  resolved: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  closed: 'bg-gray-100 text-gray-600 border-gray-200',
};

const PRIORITY_BADGE: Record<Priority, string> = {
  urgent: 'bg-red-100 text-red-800 border-red-200',
  high: 'bg-orange-100 text-orange-800 border-orange-200',
  medium: 'bg-blue-100 text-blue-800 border-blue-200',
  low: 'bg-gray-100 text-gray-600 border-gray-200',
};

const STATUS_FILTERS: { key: 'all' | Status; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'open', label: 'Open' },
  { key: 'in_progress', label: 'In progress' },
  { key: 'resolved', label: 'Resolved' },
  { key: 'closed', label: 'Closed' },
];

function fmtDate(s: string | null | undefined): string {
  if (!s) return '-';
  const d = new Date(s);
  if (isNaN(d.getTime())) return '';
  const today = new Date();
  const isToday = d.toDateString() === today.toDateString();
  return d.toLocaleString('en-GB', isToday
    ? { hour: '2-digit', minute: '2-digit' }
    : { month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' }
  );
}

function timeAgo(s: string | null | undefined): string {
  if (!s) return '';
  const d = new Date(s);
  if (isNaN(d.getTime())) return '';
  const diff = Date.now() - d.getTime();
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d`;
  return d.toLocaleDateString('en-GB', { month: 'short', day: '2-digit' });
}

const Tickets: React.FC = () => {
  const { t } = useLocalization();
  const { user } = useAuth();

  const [tickets, setTickets] = useState<TicketData[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'all' | Status>('all');
  const [search, setSearch] = useState('');

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<TicketData | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [reply, setReply] = useState('');
  const [replyAttachments, setReplyAttachments] = useState<Attachment[]>([]);
  const [attachError, setAttachError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const replyFileRef = useRef<HTMLInputElement>(null);

  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [categories, setCategories] = useState<string[]>([]);
  const [newTicket, setNewTicket] = useState<CreateTicketRequest>({
    subject: '', description: '', email: '', priority: 'medium', category: 'Technical',
  });
  const [createAttachments, setCreateAttachments] = useState<Attachment[]>([]);
  const [createError, setCreateError] = useState<string | null>(null);
  const createFileRef = useRef<HTMLInputElement>(null);

  const loadList = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const data = await ticketService.getTickets();
      setTickets(data);
    } catch (err) {
      console.error('Tickets load error:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  const loadDetail = useCallback(async (ticketId: string) => {
    setDetailLoading(true);
    try {
      const data = await ticketService.getTicket(ticketId);
      setDetail(data);
    } catch (err) {
      console.error('Ticket detail error:', err);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const loadCategories = useCallback(async () => {
    try {
      const cats = await ticketService.getCategories();
      setCategories(cats);
      if (cats.length && !cats.includes(newTicket.category)) {
        setNewTicket((p) => ({ ...p, category: cats[0] }));
      }
    } catch { /* */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { loadList(); loadCategories(); }, [loadList, loadCategories]);

  // Refresh list every 60s; refresh open ticket every 30s.
  useEffect(() => {
    const id = setInterval(() => loadList(true), 60000);
    return () => clearInterval(id);
  }, [loadList]);
  useEffect(() => {
    if (!selectedId) return;
    const id = setInterval(() => loadDetail(selectedId), 30000);
    return () => clearInterval(id);
  }, [selectedId, loadDetail]);

  const openTicket = (tk: TicketData) => {
    setSelectedId(tk.ticket_id);
    setDetail(tk);
    loadDetail(tk.ticket_id);
    // Optimistically clear the unread flag locally.
    setTickets((prev) => prev.map((x) => x.ticket_id === tk.ticket_id ? { ...x, unread_by_merchant: false } : x));
  };

  const closeDetail = () => {
    setSelectedId(null);
    setDetail(null);
    loadList(true);
  };

  const handlePickFiles = async (
    files: FileList | null,
    current: Attachment[],
    setter: (next: Attachment[]) => void
  ) => {
    setAttachError(null);
    if (!files || files.length === 0) return;
    const room = MAX_ATTACHMENTS - current.length;
    if (room <= 0) {
      setAttachError(t('tickets.attachmentTooMany'));
      return;
    }
    const accepted: Attachment[] = [];
    for (const f of Array.from(files).slice(0, room)) {
      if (f.size > MAX_FILE_BYTES) {
        setAttachError(`${f.name} — ${t('tickets.attachmentTooLarge')}`);
        continue;
      }
      try {
        const data_url = await readAsDataUrl(f);
        accepted.push({ name: f.name, type: f.type || 'application/octet-stream', size: f.size, data_url });
      } catch { /* skip */ }
    }
    setter([...current, ...accepted]);
  };

  const sendReply = async () => {
    if (!detail) return;
    if (!reply.trim() && replyAttachments.length === 0) return;
    setSending(true);
    try {
      await ticketService.sendMessage(detail.ticket_id, {
        message: reply.trim() || (replyAttachments.length > 0 ? '(Pièces jointes)' : ''),
        attachments: replyAttachments.length > 0 ? replyAttachments : undefined,
      });
      setReply('');
      setReplyAttachments([]);
      await loadDetail(detail.ticket_id);
      loadList(true);
    } catch (err) {
      console.error('Send message error:', err);
    } finally {
      setSending(false);
    }
  };

  const submitNewTicket = async () => {
    setCreateError(null);
    if (!newTicket.subject.trim() || !newTicket.description.trim() || !newTicket.email.trim()) {
      setCreateError(t('tickets.requiredFields'));
      return;
    }
    // Validation simple email (HTML5-style côté backend pydantic EmailStr fait le reste)
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRe.test(newTicket.email.trim())) {
      setCreateError(t('tickets.invalidEmail') || 'Adresse email invalide');
      return;
    }
    setCreating(true);
    try {
      const created = await ticketService.createTicket({
        subject: newTicket.subject.trim(),
        description: newTicket.description.trim(),
        email: newTicket.email.trim(),
        priority: newTicket.priority,
        category: newTicket.category,
      });
      // Attach files as a follow-up message (support_tickets has no attachments column).
      if (createAttachments.length > 0 && created?.ticket_id) {
        try {
          await ticketService.sendMessage(created.ticket_id, {
            message: '(Pièces jointes)',
            attachments: createAttachments,
          });
        } catch (err) {
          console.error('attach on create failed:', err);
        }
      }
      setShowCreate(false);
      setNewTicket({ subject: '', description: '', email: '', priority: 'medium', category: categories[0] || 'Technical' });
      setCreateAttachments([]);
      setAttachError(null);
      await loadList();
      if (created?.ticket_id) {
        setSelectedId(created.ticket_id);
        loadDetail(created.ticket_id);
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Could not create ticket';
      setCreateError(msg);
    } finally {
      setCreating(false);
    }
  };

  // Filter list client-side.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return tickets.filter((tk) => {
      if (statusFilter !== 'all' && tk.status !== statusFilter) return false;
      if (q) {
        const hay = `${tk.subject} ${tk.ticket_id} ${tk.description} ${tk.category}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [tickets, statusFilter, search]);

  const stats = useMemo(() => {
    const out = { total: tickets.length, open: 0, in_progress: 0, resolved: 0, urgent: 0, unread: 0 };
    for (const tk of tickets) {
      if (tk.status === 'open') out.open++;
      if (tk.status === 'in_progress') out.in_progress++;
      if (tk.status === 'resolved') out.resolved++;
      if (tk.priority === 'urgent') out.urgent++;
      if (tk.unread_by_merchant) out.unread++;
    }
    return out;
  }, [tickets]);

  if (loading && tickets.length === 0) {
    return <LoadingScreen fullscreen={false} message={t('tickets.loading') || 'Loading tickets...'} />;
  }

  return (
    <div className="max-w-7xl mx-auto space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <LifeBuoy size={22} className="text-emerald-600" />
            {t('tickets.title') || 'Support Tickets'}
          </h1>
          <p className="text-xs text-gray-500 mt-0.5">
            {t('tickets.subtitle') || 'Get help from the Moko Afrika support team.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => loadList()}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 text-xs hover:bg-gray-50"
          >
            <RefreshCw size={13} className={refreshing ? 'animate-spin' : ''} />
            {t('common.refresh') || 'Refresh'}
          </button>
          <button
            onClick={() => { setNewTicket((p) => ({ ...p, email: '' })); setShowCreate(true); }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-700"
          >
            <Plus size={13} /> {t('tickets.newTicket') || 'New ticket'}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KPI label={t('tickets.unreadReplies') || 'Unread replies'} value={stats.unread} tone="amber" icon={<Inbox size={14} />} />
        <KPI label={t('tickets.open') || 'Open'} value={stats.open} tone="amber" icon={<AlertCircle size={14} />} />
        <KPI label={t('tickets.inProgress') || 'In progress'} value={stats.in_progress} tone="blue" icon={<Loader2 size={14} />} />
        <KPI label={t('tickets.urgent') || 'Urgent'} value={stats.urgent} tone="red" icon={<AlertCircle size={14} />} />
        <KPI label={t('tickets.resolved') || 'Resolved'} value={stats.resolved} tone="emerald" icon={<CheckCircle size={14} />} />
      </div>

      {/* List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-3 border-b border-gray-100 flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1">
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => setStatusFilter(f.key)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  statusFilter === f.key ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="relative ml-auto flex-1 max-w-xs">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('tickets.searchPlaceholder') || 'Search subject, id, description...'}
              className="w-full h-8 pl-8 pr-3 rounded-lg border border-gray-200 text-xs outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-2.5 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider w-8"></th>
                <th className="px-4 py-2.5 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">{t('tickets.colSubject') || 'Subject'}</th>
                <th className="px-4 py-2.5 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">{t('tickets.colCategory') || 'Category'}</th>
                <th className="px-4 py-2.5 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">{t('tickets.colPriority') || 'Priority'}</th>
                <th className="px-4 py-2.5 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">{t('tickets.colStatus') || 'Status'}</th>
                <th className="px-4 py-2.5 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">{t('tickets.colActivity') || 'Activity'}</th>
                <th className="px-4 py-2.5 w-8"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {filtered.map((tk) => {
                const lastActivity = tk.last_staff_message_at || tk.last_user_message_at || tk.updated_at || tk.created_at;
                const isUnread = !!tk.unread_by_merchant;
                return (
                  <tr
                    key={tk.ticket_id}
                    onClick={() => openTicket(tk)}
                    className={`cursor-pointer hover:bg-emerald-50/30 transition-colors ${isUnread ? 'bg-amber-50/30' : ''}`}
                  >
                    <td className="px-4 py-2.5">
                      {isUnread && <span className="w-2 h-2 rounded-full bg-amber-500 inline-block" title="Unread reply" />}
                    </td>
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      <div className={`text-xs ${isUnread ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>{tk.subject}</div>
                      <div className="text-[10px] text-gray-400 font-mono">{tk.ticket_id}</div>
                    </td>
                    <td className="px-4 py-2.5 whitespace-nowrap text-xs text-gray-600">{tk.category}</td>
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-medium border uppercase ${PRIORITY_BADGE[tk.priority]}`}>
                        {tk.priority}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-medium border uppercase ${STATUS_BADGE[tk.status]}`}>
                        {tk.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 whitespace-nowrap text-[11px] text-gray-500">
                      <span title={fmtDate(lastActivity)}>{timeAgo(lastActivity)}</span>
                      <span className="ml-1 text-gray-400">· {tk.message_count} msg{tk.message_count === 1 ? '' : 's'}</span>
                    </td>
                    <td className="px-4 py-2.5 text-right text-gray-300">›</td>
                  </tr>
                );
              })}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-16 text-center text-gray-400">
                    <Inbox size={28} className="mx-auto text-gray-300 mb-2" />
                    {t('tickets.empty') || 'No tickets here yet.'}
                    <div className="mt-3">
                      <button onClick={() => { setNewTicket((p) => ({ ...p, email: '' })); setShowCreate(true); }} className="text-xs text-emerald-700 font-medium hover:underline">
                        {t('tickets.newTicket') || 'Create your first ticket'} →
                      </button>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail drawer */}
      {selectedId && (
        <div className="fixed inset-0 z-40 flex">
          <div className="flex-1 bg-black/40" onClick={closeDetail} />
          <div className="w-full max-w-2xl bg-gray-50 h-full overflow-y-auto shadow-2xl flex flex-col">
            <div className="bg-white border-b border-gray-200 px-5 py-3 flex items-center justify-between sticky top-0 z-10">
              <div className="flex items-center gap-2 min-w-0">
                <button onClick={closeDetail} className="p-1.5 rounded hover:bg-gray-100 text-gray-500">
                  <ArrowLeft size={16} />
                </button>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-gray-900 truncate">{detail?.subject || '...'}</div>
                  <div className="text-[11px] text-gray-500 font-mono">{detail?.ticket_id}</div>
                </div>
              </div>
              <button onClick={closeDetail} className="p-1.5 rounded hover:bg-gray-100 text-gray-500">
                <X size={16} />
              </button>
            </div>

            {detailLoading && !detail ? (
              <LoadingScreen fullscreen={false} message={t('tickets.loadingDetail') || 'Loading ticket...'} />
            ) : detail ? (
              <>
                {/* Meta */}
                <div className="bg-white border-b border-gray-200 p-5 space-y-3">
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <Field label={t('tickets.colCategory') || 'Category'} value={detail.category} />
                    <Field
                      label={t('tickets.colPriority') || 'Priority'}
                      value={
                        <span className={`px-2 py-0.5 rounded text-[10px] font-medium border uppercase ${PRIORITY_BADGE[detail.priority]}`}>
                          {detail.priority}
                        </span>
                      }
                    />
                    <Field label={t('tickets.opened') || 'Opened'} value={fmtDate(detail.created_at)} sub={`by ${detail.created_by}`} />
                    <Field
                      label={t('tickets.colStatus') || 'Status'}
                      value={
                        <span className={`px-2 py-0.5 rounded text-[10px] font-medium border uppercase ${STATUS_BADGE[detail.status]}`}>
                          {detail.status.replace('_', ' ')}
                        </span>
                      }
                    />
                    {detail.assigned_to && (
                      <Field label={t('tickets.assignedTo') || 'Assigned to'} value={detail.assigned_to} />
                    )}
                  </div>

                  {/* Original description */}
                  <div className="pt-2 border-t border-gray-100">
                    <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">
                      {t('tickets.originalDescription') || 'Original description'}
                    </div>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{detail.description}</p>
                  </div>
                </div>

                {/* Thread */}
                <div className="flex-1 px-5 py-4 space-y-3">
                  {detail.messages && detail.messages.length > 0 ? (
                    detail.messages.map((m) => (
                      <Bubble
                        key={m.id}
                        sender_type={m.sender_type}
                        sender_name={m.sender_name}
                        message={m.message}
                        attachments={m.attachments as Attachment[] | undefined}
                        created_at={m.created_at}
                      />
                    ))
                  ) : (
                    <div className="text-center py-8 text-xs text-gray-400">
                      {t('tickets.noReplies')}
                    </div>
                  )}
                </div>

                {/* Reply composer (disabled when closed/resolved) */}
                <div className="bg-white border-t border-gray-200 p-4 sticky bottom-0">
                  {detail.status === 'closed' || detail.status === 'resolved' ? (
                    <div className="text-xs text-gray-500 text-center py-2">
                      {t('tickets.threadClosed')}
                    </div>
                  ) : (
                    <>
                      <textarea
                        value={reply}
                        onChange={(e) => setReply(e.target.value)}
                        placeholder={t('tickets.replyPlaceholder')}
                        rows={4}
                        onKeyDown={(e) => {
                          if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') sendReply();
                        }}
                        className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 resize-none"
                      />

                      {replyAttachments.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {replyAttachments.map((a, i) => (
                            <AttachmentChip
                              key={i} att={a}
                              onRemove={() => setReplyAttachments((p) => p.filter((_, idx) => idx !== i))}
                            />
                          ))}
                        </div>
                      )}
                      {attachError && (
                        <div className="mt-2 text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1">
                          {attachError}
                        </div>
                      )}

                      <div className="flex items-center justify-between mt-2 gap-2">
                        <div className="flex items-center gap-2">
                          <input
                            ref={replyFileRef}
                            type="file" multiple
                            onChange={(e) => { void handlePickFiles(e.target.files, replyAttachments, setReplyAttachments); e.target.value = ''; }}
                            className="hidden"
                          />
                          <button
                            type="button"
                            onClick={() => replyFileRef.current?.click()}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-gray-300 text-xs text-gray-700 hover:bg-gray-50"
                          >
                            <Paperclip size={13} /> {t('tickets.attachFiles')}
                          </button>
                          <span className="text-[10px] text-gray-400">{t('tickets.attachmentHint')}</span>
                        </div>
                        <button
                          onClick={sendReply}
                          disabled={(!reply.trim() && replyAttachments.length === 0) || sending}
                          className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-700 disabled:opacity-50"
                        >
                          {sending ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                          {sending ? t('tickets.sending') : t('tickets.sendReply')}
                        </button>
                      </div>
                      <p className="text-[10px] text-gray-400 mt-1">⌘/Ctrl + Enter</p>
                    </>
                  )}
                </div>
              </>
            ) : (
              <div className="text-center py-12 text-sm text-gray-500">{t('tickets.notFound') || 'Ticket not found.'}</div>
            )}
          </div>
        </div>
      )}

      {/* Create modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{t('tickets.newTicket')}</h3>
                <p className="text-xs text-gray-500 mt-0.5">{t('tickets.subtitle')}</p>
              </div>
              <button onClick={() => setShowCreate(false)} className="p-1.5 rounded hover:bg-gray-100">
                <X size={18} className="text-gray-500" />
              </button>
            </div>
            <div className="p-6 space-y-4 overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">{t('tickets.colCategory')}</label>
                  <select
                    value={newTicket.category}
                    onChange={(e) => setNewTicket((p) => ({ ...p, category: e.target.value }))}
                    className="w-full h-10 px-3 rounded-lg border border-gray-300 text-sm bg-white outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
                  >
                    {(categories.length ? categories : ['Technical']).map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">{t('tickets.colPriority')}</label>
                  <select
                    value={newTicket.priority}
                    onChange={(e) => setNewTicket((p) => ({ ...p, priority: e.target.value as Priority }))}
                    className="w-full h-10 px-3 rounded-lg border border-gray-300 text-sm bg-white outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  {t('tickets.emailLabel') || 'Email de contact'} <span className="text-rose-600">*</span>
                </label>
                <input
                  type="email"
                  value={newTicket.email}
                  onChange={(e) => setNewTicket((p) => ({ ...p, email: e.target.value }))}
                  placeholder="vous@entreprise.com"
                  required
                  className="w-full h-10 px-3 rounded-lg border border-gray-300 text-sm outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
                />
                <p className="text-[11px] text-gray-500 mt-1">
                  {t('tickets.emailHint') || "Nous vous contacterons à cette adresse à chaque changement de statut ou réponse de l'équipe support."}
                </p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">{t('tickets.colSubject')}</label>
                <input
                  value={newTicket.subject}
                  onChange={(e) => setNewTicket((p) => ({ ...p, subject: e.target.value }))}
                  placeholder={t('tickets.subjectPlaceholder')}
                  className="w-full h-10 px-3 rounded-lg border border-gray-300 text-sm outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">{t('tickets.descriptionLabel')}</label>
                <textarea
                  value={newTicket.description}
                  onChange={(e) => setNewTicket((p) => ({ ...p, description: e.target.value }))}
                  placeholder={t('tickets.descriptionPlaceholder')}
                  rows={8}
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 resize-none"
                />
              </div>

              {/* Attachments */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  {t('tickets.attachmentsSection')}
                  <span className="ml-1 text-gray-400 font-normal">— {t('tickets.attachmentHint')}</span>
                </label>
                <input
                  ref={createFileRef}
                  type="file" multiple
                  onChange={(e) => { void handlePickFiles(e.target.files, createAttachments, setCreateAttachments); e.target.value = ''; }}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => createFileRef.current?.click()}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border-2 border-dashed border-gray-300 text-xs text-gray-600 hover:border-emerald-400 hover:text-emerald-700 hover:bg-emerald-50 transition-colors"
                >
                  <Paperclip size={14} /> {t('tickets.attachFiles')}
                </button>
                {createAttachments.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {createAttachments.map((a, i) => (
                      <AttachmentChip
                        key={i} att={a}
                        onRemove={() => setCreateAttachments((p) => p.filter((_, idx) => idx !== i))}
                      />
                    ))}
                  </div>
                )}
                {attachError && (
                  <div className="mt-2 text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1">
                    {attachError}
                  </div>
                )}
              </div>

              {createError && (
                <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-2.5">{createError}</div>
              )}
            </div>
            <div className="px-6 py-3 border-t border-gray-100 flex justify-end gap-2">
              <button
                onClick={() => setShowCreate(false)}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm hover:bg-gray-50"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={submitNewTicket}
                disabled={creating}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50"
              >
                {creating ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                {creating ? t('tickets.submitting') : t('tickets.submit')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

function KPI({ label, value, tone, icon }: { label: string; value: number; tone: 'amber' | 'emerald' | 'blue' | 'red'; icon: React.ReactNode }) {
  const tones: Record<string, { bg: string; text: string }> = {
    amber:   { bg: 'bg-amber-50',   text: 'text-amber-700' },
    emerald: { bg: 'bg-emerald-50', text: 'text-emerald-700' },
    blue:    { bg: 'bg-blue-50',    text: 'text-blue-700' },
    red:     { bg: 'bg-red-50',     text: 'text-red-700' },
  };
  const t = tones[tone];
  return (
    <div className="bg-white rounded-xl border border-gray-200 px-3 py-2.5">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">{label}</span>
        <div className={`w-6 h-6 rounded ${t.bg} ${t.text} flex items-center justify-center`}>{icon}</div>
      </div>
      <div className="mt-1 text-xl font-bold text-gray-900">{value.toLocaleString()}</div>
    </div>
  );
}

function Field({ label, value, sub }: { label: string; value: React.ReactNode; sub?: string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-gray-500">{label}</div>
      <div className="font-medium text-gray-900">{value}</div>
      {sub && <div className="text-[10px] text-gray-400">{sub}</div>}
    </div>
  );
}

function Bubble({ sender_type, sender_name, message, attachments, created_at }: {
  sender_type: 'user' | 'staff';
  sender_name: string;
  message: string;
  attachments?: Attachment[] | null;
  created_at: string;
}) {
  const isStaff = sender_type === 'staff';
  const list: Attachment[] = Array.isArray(attachments) ? attachments : [];
  return (
    <div className={`flex ${isStaff ? 'justify-start' : 'justify-end'}`}>
      <div className={`max-w-[85%] ${isStaff ? 'bg-white border border-gray-200 text-gray-800' : 'bg-emerald-600 text-white'} rounded-2xl px-3.5 py-2.5 shadow-sm`}>
        <div className={`flex items-center gap-1.5 mb-1 text-[10px] ${isStaff ? 'text-gray-500' : 'text-emerald-100'}`}>
          {isStaff
            ? <span className="font-semibold inline-flex items-center gap-1"><MessageSquare size={10} /> Moko Support · {sender_name}</span>
            : <span className="font-semibold">You · {sender_name}</span>}
          <span>· {fmtDate(created_at)}</span>
        </div>
        {message && <p className="text-sm whitespace-pre-wrap">{message}</p>}
        {list.length > 0 && (
          <div className={`mt-2 flex flex-wrap gap-2 ${message ? 'pt-2 border-t' : ''} ${isStaff ? 'border-gray-200' : 'border-emerald-500/40'}`}>
            {list.map((a, i) => <AttachmentView key={i} att={a} dark={!isStaff} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function AttachmentChip({ att, onRemove }: { att: Attachment; onRemove: () => void }) {
  const isImage = att.type.startsWith('image/');
  return (
    <div className="inline-flex items-center gap-2 pl-2 pr-1 py-1 rounded-lg border border-gray-200 bg-white text-xs">
      {isImage ? (
        <img src={att.data_url} alt={att.name} className="w-7 h-7 object-cover rounded" />
      ) : (
        <span className="w-7 h-7 rounded bg-gray-100 text-gray-500 flex items-center justify-center">
          <FileText size={14} />
        </span>
      )}
      <span className="text-gray-700 truncate max-w-[160px]">{att.name}</span>
      <span className="text-gray-400 text-[10px]">{formatBytes(att.size)}</span>
      <button
        type="button"
        onClick={onRemove}
        className="p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-600"
        title="Remove"
      >
        <X size={12} />
      </button>
    </div>
  );
}

function AttachmentView({ att, dark }: { att: Attachment; dark?: boolean }) {
  const isImage = att.type.startsWith('image/');
  if (isImage) {
    return (
      <a href={att.data_url} download={att.name} target="_blank" rel="noopener noreferrer" className="block">
        <img
          src={att.data_url}
          alt={att.name}
          className="max-w-[220px] max-h-[180px] rounded-lg border border-black/10 object-cover hover:opacity-90 transition-opacity"
        />
      </a>
    );
  }
  return (
    <a
      href={att.data_url}
      download={att.name}
      className={`inline-flex items-center gap-2 px-2.5 py-1.5 rounded-lg border text-xs ${
        dark ? 'bg-emerald-700/40 border-emerald-300/30 text-white' : 'bg-gray-50 border-gray-200 text-gray-700'
      }`}
    >
      {att.type.startsWith('image/') ? <ImageIcon size={14} /> : <FileText size={14} />}
      <span className="truncate max-w-[160px]">{att.name}</span>
      <span className={dark ? 'text-emerald-100' : 'text-gray-400'}>· {formatBytes(att.size)}</span>
      <Download size={12} className={dark ? 'text-emerald-100' : 'text-gray-400'} />
    </a>
  );
}

export default Tickets;
